/**
 * ============================================================
 * KEJJA OS — src/pages/library/ReadAttachmentModal.tsx
 * Show extracted text from a PDF attachment.
 * ============================================================
 */

import { useState } from 'react';
import { X, Copy, Check, Download, Search } from 'lucide-react';
import { api } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
import { libraryApi } from '@/lib/libraryApi';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface Props {
  homeId: string;
  attachmentId: string;
  fileName: string;
  onClose: () => void;
}

export default function ReadAttachmentModal({
  homeId, attachmentId, fileName, onClose,
}: Props) {
  const [query, setQuery] = useState('');
  const [copied, setCopied] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['library', homeId, 'attachment', attachmentId, 'text'],
    queryFn: () => api.get<{ text: string; fileName: string }>(
      `/homes/${homeId}/library/attachments/${attachmentId}/read`,
    ),
  });

  const text = data?.text ?? '';
  const filtered = query.trim()
    ? text.split('\n').filter((l) => l.toLowerCase().includes(query.toLowerCase())).join('\n')
    : text;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <Card className="w-full max-w-3xl my-8 max-h-[calc(100vh-4rem)] overflow-hidden flex flex-col">
        <CardContent className="p-4 flex flex-col min-h-0 flex-1">
          <div className="flex items-center justify-between gap-2 pb-3 border-b border-border">
            <div className="min-w-0 flex-1">
              <h2 className="font-bold text-sm truncate">{fileName}</h2>
              <p className="text-[10px] text-muted-foreground">Extracted text</p>
            </div>
            <Button size="sm" variant="ghost" onClick={handleCopy} disabled={!text}>
              {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
            </Button>
            <a
              href={libraryApi.downloadUrl(homeId, attachmentId)}
              target="_blank" rel="noreferrer"
              className="inline-flex items-center justify-center h-9 px-3 rounded-md text-sm hover:bg-muted"
            >
              <Download className="w-4 h-4" />
            </a>
            <Button size="sm" variant="ghost" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>

          {text && (
            <div className="relative pt-3 pb-2 flex-shrink-0">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/4 w-3.5 h-3.5 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search in text…"
                className="pl-8 h-8 text-xs"
              />
            </div>
          )}

          <div className="flex-1 overflow-y-auto mt-2">
            {isLoading ? (
              <div className="text-center text-xs text-muted-foreground py-8">Loading text…</div>
            ) : error ? (
              <div className="text-center text-xs text-rose-600 py-8">
                Could not load text. {(error as Error).message}
              </div>
            ) : !text ? (
              <div className="text-center text-xs text-muted-foreground py-12">
                No text could be extracted from this file.
                {' '}It may be a scanned image PDF, or an unsupported format.
              </div>
            ) : (
              <pre className="text-xs whitespace-pre-wrap font-sans leading-relaxed p-3 bg-muted/30 rounded-md">
                {filtered}
              </pre>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
